import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection, TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { fromDraftNotes, toNotesBody } from '../api/mappers';
import { useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { isStateConflict } from '../schemas/draft-order-rules';
import { notesSchema, type NotesFormInput, type NotesFormOutput } from '../schemas/notes-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['note', 'staffNotes', 'tags']);

export interface DraftNotesCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

export function DraftNotesCard({ storeId, draft, editable }: DraftNotesCardProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:notes.saved');
  const form = useForm<NotesFormInput, unknown, NotesFormOutput>({
    resolver: useI18nResolver(notesSchema),
    defaultValues: fromDraftNotes(draft),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({ id: draft.id, body: toNotesBody(values) });
      form.reset(values);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  if (!editable) {
    return (
      <FormSection title={t('draftOrders:notes.title')}>
        <dl className="grid gap-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">{t('draftOrders:notes.note')}</dt>
            <dd className="whitespace-pre-line">{draft.note ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">{t('draftOrders:notes.staffNotes')}</dt>
            <dd className="whitespace-pre-line">{draft.staffNotes ?? '—'}</dd>
          </div>
          {draft.tags.length > 0 ? (
            <div>
              <dt className="text-muted-foreground text-xs">{t('draftOrders:notes.tags')}</dt>
              <dd className="flex flex-wrap gap-1 pt-1">
                {draft.tags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </dd>
            </div>
          ) : null}
        </dl>
      </FormSection>
    );
  }

  return (
    <FormSection title={t('draftOrders:notes.title')}>
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('draftOrders:notes.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:notes.note')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormDescription>{t('draftOrders:notes.noteHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="staffNotes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:notes.staffNotes')}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormDescription>{t('draftOrders:notes.staffNotesHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="tags"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('draftOrders:notes.tags')}</FormLabel>
                <FormControl>
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    max={50}
                    maxLength={60}
                    placeholder={t('draftOrders:notes.tagsPlaceholder')}
                    removeLabel={(tag) => t('draftOrders:notes.removeTag', { tag })}
                    aria-invalid={fieldState.invalid}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div>
            <Button type="submit" disabled={update.isPending || !form.formState.isDirty}>
              {update.isPending ? t('common:actions.saving') : t('draftOrders:notes.save')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
